import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh,
  Info,
  CheckCircle,
  Error,
  Schedule,
  PlayArrow,
  Stop,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  useGetJobRunsQuery,
  useGetJobRunStatsQuery,
  useCancelJobRunMutation
} from '../store/api/jobRunsApi';


const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'failed':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle />;
    case 'in_progress':
      return <PlayArrow />;
    case 'failed':
      return <Error />;
    case 'pending':
      return <Schedule />;
    default:
      return <Info />;
  }
};

const formatDateTime = (dateString: string, userTimezone: string = 'US/Eastern') => {
  if (!dateString) return 'N/A';
  const utcDate = new Date(dateString);
  
  // Use Intl.DateTimeFormat for proper timezone conversion
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const formattedDate = parts.map(part => part.value).join('');
  
  // Get timezone abbreviation
  const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    timeZoneName: 'short'
  });
  
  const timeZoneName = timeZoneFormatter.formatToParts(utcDate)
    .find(part => part.type === 'timeZoneName')?.value || '';
  
  return `${formattedDate} ${timeZoneName}`;
};

const getDuration = (startedAt: string, completedAt: string | null) => {
  if (!startedAt) return 'N/A';
  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();
  const durationSeconds = Math.floor(durationMs / 1000);
  
  if (durationSeconds < 60) {
    return `${durationSeconds}s`;
  } else {
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }
};

export const TwilioJobRunsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [selectedJobRun, setSelectedJobRun] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { user } = useSelector((state: any) => state.auth);
  const userTimezone = user?.timezone || 'US/Eastern';

  // API queries
  const { 
    data: jobRunsData, 
    isLoading: jobRunsLoading, 
    error: jobRunsError,
    refetch: refetchJobRuns
  } = useGetJobRunsQuery({
    status: statusFilter !== 'all' ? statusFilter as any : undefined,
    job_type: jobTypeFilter !== 'all' ? jobTypeFilter as any : undefined,
    page,
    page_size: 20
  });

  const { 
    data: jobRunStats
  } = useGetJobRunStatsQuery({});

  const [cancelJobRun, { isLoading: cancelLoading }] = useCancelJobRunMutation();

  const handleRefresh = () => {
    refetchJobRuns();
  };

  const handleViewDetails = (jobRun: any) => {
    setSelectedJobRun(jobRun);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedJobRun(null);
  };

  const handleCancelJob = async (jobRunId: number) => {
    try {
      await cancelJobRun({ jobRunId }).unwrap();
      // Refresh the job runs list
      refetchJobRuns();
    } catch (error) {
      console.error('Failed to cancel job:', error);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Twilio Sync Job Runs
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={jobRunsLoading}
        >
          {jobRunsLoading ? <CircularProgress size={20} /> : 'Refresh'}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Track all Twilio sync operations - both manual and automated
      </Typography>

      {/* Statistics */}
      {jobRunStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Job Run Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="h4" color="primary">
                  {jobRunStats.total_runs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Runs
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="h4" color="success.main">
                  {jobRunStats.successful_runs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successful
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="h4" color="error.main">
                  {jobRunStats.failed_runs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="h4" color="info.main">
                  {jobRunStats.in_progress_runs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Progress
                </Typography>
              </Grid>
            </Grid>
            {jobRunStats.last_run_at && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Last Run: {formatDateTime(jobRunStats.last_run_at, userTimezone)}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  value={jobTypeFilter}
                  onChange={(e) => setJobTypeFilter(e.target.value)}
                  label="Job Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automated">Automated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Job Runs Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Job ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Calls</TableCell>
                  <TableCell>Cancel</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobRunsLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : jobRunsError ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Alert severity="error">
                        Error loading job runs: {JSON.stringify(jobRunsError)}
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : jobRunsData?.job_runs?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary">
                        No job runs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobRunsData?.job_runs?.map((jobRun) => (
                  <TableRow key={jobRun.id}>
                    <TableCell>{jobRun.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={jobRun.job_type}
                        color={jobRun.job_type === 'manual' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(jobRun.status)}
                        label={jobRun.status}
                        color={getStatusColor(jobRun.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(jobRun.started_at || '', userTimezone)}
                    </TableCell>
                    <TableCell>
                      {getDuration(jobRun.started_at || '', jobRun.completed_at)}
                    </TableCell>
                    <TableCell>
                      {jobRun.messages_synced} / {jobRun.total_messages}
                    </TableCell>
                    <TableCell>
                      {jobRun.calls_synced} / {jobRun.total_calls}
                    </TableCell>
                    <TableCell>
                      {(jobRun.status === 'pending' || jobRun.status === 'in_progress') && (
                        <Tooltip title="Cancel Job">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelJob(jobRun.id)}
                            disabled={cancelLoading}
                          >
                            <Stop />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(jobRun)}
                        >
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {jobRunsData && jobRunsData.total_pages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={jobRunsData.total_pages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          Job Run Details - #{selectedJobRun?.id}
        </DialogTitle>
        <DialogContent>
          {selectedJobRun && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Job Information
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedJobRun.job_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedJobRun.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {formatDateTime(selectedJobRun.created_at, userTimezone)}
                </Typography>
                <Typography variant="body2">
                  <strong>Started:</strong> {formatDateTime(selectedJobRun.started_at, userTimezone)}
                </Typography>
                <Typography variant="body2">
                  <strong>Completed:</strong> {formatDateTime(selectedJobRun.completed_at, userTimezone)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Sync Results
                </Typography>
                <Typography variant="body2">
                  <strong>Messages Synced:</strong> {selectedJobRun.messages_synced}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Messages:</strong> {selectedJobRun.total_messages}
                </Typography>
                <Typography variant="body2">
                  <strong>Calls Synced:</strong> {selectedJobRun.calls_synced}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Calls:</strong> {selectedJobRun.total_calls}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {getDuration(selectedJobRun.started_at, selectedJobRun.completed_at)}
                </Typography>
              </Grid>
              {selectedJobRun.error_message && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="subtitle2">Error:</Typography>
                    <Typography variant="body2">{selectedJobRun.error_message}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
